import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import { connect } from "react-redux";
import { compose } from "redux";
import PropTypes from "prop-types";
import LogoLight from "../../Components/LogoLight";
import actions from "../../Stores/Auth/actions";

const verify2FAAction = actions.verify2FA;
const resend2FAAction = actions.resend2FA;

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: "6rem",
  },
  paper: {
    padding: "2rem",
  },
  title: {
    marginBottom: "1rem",
    fontWeight: 600,
  },
  subtitle: {
    marginBottom: "2rem",
    color: theme.palette.text.secondary,
  },
  codeInput: {
    "& input": {
      textAlign: "center",
      fontSize: "2rem",
      letterSpacing: "0.5rem",
      fontWeight: 600,
    },
  },
  buttonContainer: {
    marginTop: "1.5rem",
    display: "flex",
    gap: "1rem",
  },
  verifyButton: {
    flex: 1,
  },
  resendButton: {
    marginTop: "1rem",
    textTransform: "none",
  },
  logoContainer: {
    width: "40%",
    marginLeft: "-1.2rem",
    marginTop: "-0.5rem",
    marginBottom: "1rem",
  },
  email: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  timer: {
    marginTop: "1rem",
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
}));

const TwoFactorVerification = ({
  email,
  rememberMe,
  verify2FA,
  resend2FA,
  isVerifying,
}) => {
  const classes = useStyles();
  const [code, setCode] = useState("");
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.length === 6) {
      verify2FA({
        email,
        code,
        remember_me: rememberMe,
        device_type: 'web',
        device_name: navigator.userAgent.split('(')[0].trim()
      });
    }
  };

  const handleResend = () => {
    resend2FA({ email });
    setResendTimer(60);
    setCanResend(false);
    setCode("");
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only digits
    if (value.length <= 6) {
      setCode(value);
    }
  };

  return (
    <Container maxWidth="xs" className={classes.container}>
      <Paper className={classes.paper}>
        <Box className={classes.logoContainer}>
          <LogoLight />
        </Box>

        <Typography variant="h5" className={classes.title}>
          Two-Factor Authentication
        </Typography>
        
        <Typography variant="body2" className={classes.subtitle}>
          We've sent a 6-digit verification code to{" "}
          <span className={classes.email}>{email}</span>
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="000000"
            value={code}
            onChange={handleCodeChange}
            autoFocus
            required
            inputProps={{
              maxLength: 6,
              pattern: "[0-9]{6}",
            }}
            className={classes.codeInput}
            helperText="Enter the 6-digit code from your email"
          />

          <Box className={classes.buttonContainer}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={code.length !== 6 || isVerifying}
              className={classes.verifyButton}
            >
              {isVerifying ? (
                <>
                  <CircularProgress size={20} color="inherit" style={{ marginRight: 8 }} />
                  Verifying...
                </>
              ) : (
                "Verify Code"
              )}
            </Button>
          </Box>

          <Box textAlign="center">
            <Button
              onClick={handleResend}
              disabled={!canResend || isVerifying}
              className={classes.resendButton}
              color="primary"
            >
              {canResend ? "Resend Code" : `Resend in ${resendTimer}s`}
            </Button>
          </Box>

          {!canResend && (
            <Typography variant="caption" className={classes.timer} align="center" display="block">
              Didn't receive the code? You can request a new one in {resendTimer} seconds.
            </Typography>
          )}
        </form>
      </Paper>
    </Container>
  );
};

TwoFactorVerification.propTypes = {
  email: PropTypes.string.isRequired,
  rememberMe: PropTypes.bool,
  verify2FA: PropTypes.func.isRequired,
  resend2FA: PropTypes.func.isRequired,
  isVerifying: PropTypes.bool,
};

const mapStateToProps = ({ AuthReducer }) => ({
  isVerifying: AuthReducer.isVerifying2FA || false,
});

const mapDispatchToProps = (dispatch) => ({
  verify2FA: (payload) => dispatch(verify2FAAction(payload)),
  resend2FA: (payload) => dispatch(resend2FAAction(payload)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(TwoFactorVerification);

